import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, BookOpenText } from "lucide-react";
import SovereignAnchor from "../components/SovereignAnchor";
import useSeo from "../hooks/useSeo";
import { getPostBySlug } from "../data/intelPosts";
import { BoardLink } from "../lib/boardLinks";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

function GoldDivider() {
    return (
        <div className="my-10 flex items-center gap-3" aria-hidden="true">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[hsl(40_61%_53%/0.7)]" />
            <span className="text-[hsl(45_85%_68%)] text-xs tracking-[0.5em]">✦</span>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[hsl(40_61%_53%/0.7)]" />
        </div>
    );
}

function PullQuote({ text, attribution }) {
    return (
        <figure className="my-12 px-6 md:px-10 py-8 border-l-2 border-[hsl(45_85%_68%)] bg-[hsl(151_55%_7%/0.5)] rounded-r-md">
            <blockquote
                className="text-2xl md:text-3xl leading-snug text-[hsl(45_85%_82%)]"
                style={{ ...cormorant, fontStyle: "italic", fontWeight: 500 }}
            >
                “{text}”
            </blockquote>
            {attribution && (
                <figcaption className="mt-4 text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
                    — {attribution}
                </figcaption>
            )}
        </figure>
    );
}

function renderBlock(block, idx) {
    switch (block.type) {
        case "h2":
            return (
                <h2
                    key={idx}
                    className="font-heading text-3xl md:text-4xl mt-12 mb-5 tracking-tight"
                >
                    {block.text}
                </h2>
            );
        case "p":
            return (
                <p
                    key={idx}
                    className="mt-5 text-lg leading-[1.85] text-foreground/85"
                >
                    {block.text}
                </p>
            );
        case "quote":
            return <PullQuote key={idx} text={block.text} attribution={block.attribution} />;
        case "ul":
            return (
                <ul key={idx} className="mt-5 space-y-3 list-none">
                    {block.items.map((item, i) => (
                        <li
                            key={i}
                            className="pl-6 relative text-lg leading-[1.7] text-foreground/85 before:absolute before:left-0 before:top-3 before:w-2 before:h-2 before:rounded-full before:bg-[hsl(45_85%_68%)]"
                        >
                            {item}
                        </li>
                    ))}
                </ul>
            );
        case "divider":
            return <GoldDivider key={idx} />;
        case "callout":
            return (
                <aside
                    key={idx}
                    className="my-10 rounded-xl border border-[hsl(45_85%_68%/0.4)] bg-[hsl(40_60%_18%/0.25)] p-6"
                >
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] mb-2">
                        {block.title}
                    </div>
                    <div className="text-base leading-relaxed text-foreground/85" style={cormorant}>
                        {block.body}
                    </div>
                </aside>
            );
        default:
            return null;
    }
}

export default function IntelPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);

    useSeo({
        title: post?.seoTitle || (post ? `${post.title} | EIGOO Intel` : "Fleet Intel | EIGOO"),
        description: post?.seoDescription,
    });

    if (!post) return <Navigate to="/intel" replace />;

    const formattedDate = new Date(post.publishedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <div className="min-h-screen bg-background text-foreground grain-overlay relative" data-testid={`intel-post-${post.slug}`}>
            <div className="absolute inset-0 pointer-events-none opacity-30">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.10)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.22)] blur-[120px]" />
            </div>

            <header className="relative border-b border-[hsl(40_40%_25%/0.2)] bg-[hsl(151_55%_7%/0.7)] backdrop-blur-md">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
                    <Link to="/intel" className="flex items-center gap-3" data-testid="intel-post-back">
                        <SovereignAnchor size={26} strokeWidth={6} className="text-[hsl(45_85%_68%)]" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">EIGOO · Sovereign Fleet</div>
                            <div className="font-heading text-2xl gold-gradient-text tracking-tight">Fleet Intel</div>
                        </div>
                    </Link>
                    <Link to="/intel" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors">
                        <ArrowLeft className="w-4 h-4" /> All briefs
                    </Link>
                </div>
            </header>

            <main className="relative max-w-3xl mx-auto px-6 py-16">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] flex items-center gap-2 mb-5">
                    <BookOpenText className="w-3 h-3" />
                    {post.heroEyebrow || post.category}
                </div>

                <h1 className="font-heading text-4xl md:text-6xl leading-[1.05] tracking-tighter">
                    {post.title}
                </h1>

                {post.heroAccent && (
                    <p
                        className="mt-6 text-2xl md:text-3xl text-foreground/80 leading-snug"
                        style={{ ...cormorant, fontStyle: "italic" }}
                        data-testid="intel-post-hero-accent"
                    >
                        {post.heroAccent}
                    </p>
                )}

                <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-muted-foreground">
                    <span>{post.author}</span>
                    <span className="w-1 h-1 rounded-full bg-[hsl(45_85%_68%)]" />
                    <span>{formattedDate}</span>
                    <span className="w-1 h-1 rounded-full bg-[hsl(45_85%_68%)]" />
                    <span>{post.readingMinutes} min</span>
                </div>

                <GoldDivider />

                <article className="prose-invert" data-testid="intel-post-body">
                    {post.blocks.map(renderBlock)}
                </article>

                <GoldDivider />

                <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
                    <Link
                        to="/intel"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        data-testid="intel-post-back-bottom"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Fleet Intel
                    </Link>
                    <BoardLink
                        className="px-5 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        data-testid="intel-post-cta-board"
                    >
                        Board the Fleet →
                    </BoardLink>
                </div>
            </main>
        </div>
    );
}
